class BuildYourBundle extends HTMLElement {
  constructor() {
    super();

    this.config = {
      minThreshold: parseFloat(this.dataset.minThreshold) || 125,
      tier1Threshold: parseFloat(this.dataset.tier1Threshold) || 125,
      tier1Discount: parseFloat(this.dataset.tier1Discount) || 5,
      tier2Threshold: parseFloat(this.dataset.tier2Threshold) || 175,
      tier2Discount: parseFloat(this.dataset.tier2Discount) || 10,
      subscribeExtra: parseFloat(this.dataset.subscribeExtra) || 5,
      sellingPlanIdTier1: this.dataset.sellingPlanIdTier1 || '',
      sellingPlanIdTier2: this.dataset.sellingPlanIdTier2 || '',
      maxItems: parseInt(this.dataset.maxItems, 10) || 0,
    };

    this.state = {
      purchaseType: 'onetime',
      items: new Map(),
    };
  }

  connectedCallback() {
    this.cacheDOM();
    this.bindEvents();
    this.updateUI();
  }

  cacheDOM() {
    this.toggleBtns = this.querySelectorAll('[data-purchase-type]');
    this.productCards = this.querySelectorAll('.byb__product-card');
    this.progressFill = this.querySelector('.byb__progress-fill');
    this.markers = this.querySelectorAll('.byb__marker');
    this.discountLabels = this.querySelectorAll('.byb__marker-discount');

    this.sidebarItems = this.querySelector('[data-sidebar-items]');
    this.sidebarProtein = this.querySelector('[data-sidebar-protein]');
    this.sidebarFiber = this.querySelector('[data-sidebar-fiber]');
    this.sidebarPhytonutrients = this.querySelector('[data-sidebar-phytonutrients]');
    this.phytonutrientsRow = this.querySelector('[data-sidebar-phytonutrients-row]');
    this.sidebarSubtotal = this.querySelector('[data-sidebar-subtotal]');
    this.sidebarDiscount = this.querySelector('[data-sidebar-discount]');
    this.sidebarTotal = this.querySelector('[data-sidebar-total]');
    this.discountRow = this.querySelector('.byb__sidebar-row--discount');
    this.thresholdMsg = this.querySelector('[data-threshold-msg]');
    this.remainingAmount = this.querySelector('[data-remaining-amount]');
    this.checkoutBtn = this.querySelector('[data-checkout-btn]');

    this.checkoutError = this.querySelector('[data-checkout-error]');
    this.maxItemsMsg = this.querySelector('[data-max-items-msg]');
    this.maxModal = this.querySelector('[data-max-modal]');
    this.maxModalCloseBtns = this.querySelectorAll('[data-max-modal-close]');

    this.mobileItems = this.querySelector('[data-mobile-items]');
    this.mobileSubtotal = this.querySelector('[data-mobile-subtotal]');
    this.mobileTotal = this.querySelector('[data-mobile-total]');
    this.mobileCheckoutBtn = this.querySelector('[data-checkout-btn-mobile]');
  }

  bindEvents() {
    this.toggleBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.setPurchaseType(btn.dataset.purchaseType));
    });

    this.productCards.forEach((card) => {
      if (card.dataset.available === 'false') return;

      const minus = card.querySelector('.byb__qty-btn--minus');
      const plus = card.querySelector('.byb__qty-btn--plus');
      const variantId = card.dataset.variantId;
      const price = parseInt(card.dataset.price, 10);

      if (plus) plus.addEventListener('click', () => this.changeQty(variantId, price, 1, card));
      if (minus) minus.addEventListener('click', () => this.changeQty(variantId, price, -1, card));
    });

    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', () => this.addToCart());
    }
    if (this.mobileCheckoutBtn) {
      this.mobileCheckoutBtn.addEventListener('click', () => this.addToCart());
    }

    if (this.maxModal) {
      this.maxModalCloseBtns.forEach((btn) => {
        btn.addEventListener('click', () => this.closeMaxModal());
      });
      this.maxModal.addEventListener('click', (event) => {
        if (event.target === this.maxModal) this.closeMaxModal();
      });
    }
  }

  showMaxModal() {
    if (this.maxModal && typeof this.maxModal.showModal === 'function' && !this.maxModal.open) {
      this.maxModal.showModal();
    }
  }

  closeMaxModal() {
    if (this.maxModal && this.maxModal.open) {
      this.maxModal.close();
    }
  }

  setPurchaseType(type) {
    if (type === 'subscribe') {
      const btn = this.querySelector('[data-purchase-type="subscribe"]');
      if (btn && btn.disabled) return;
    }

    this.state.purchaseType = type;

    this.toggleBtns.forEach((btn) => {
      btn.classList.toggle('byb__toggle-btn--active', btn.dataset.purchaseType === type);
    });

    this.updateDiscountLabels();
    this.updateUI();
  }

  updateDiscountLabels() {
    const isSubscribe = this.state.purchaseType === 'subscribe';
    this.discountLabels.forEach((label) => {
      if (!label.dataset.onetime) return;
      const pct = isSubscribe ? label.dataset.subscribe : label.dataset.onetime;
      label.textContent = `${pct}% OFF`;
    });
  }

  changeQty(variantId, price, delta, card) {
    const current = this.state.items.get(variantId) || { qty: 0, price };
    const newQty = Math.max(0, current.qty + delta);

    if (delta > 0 && this.config.maxItems > 0) {
      const totalWithoutThis = this.getTotalItems() - (current.qty || 0);
      if (totalWithoutThis + newQty > this.config.maxItems) {
        this.showMaxModal();
        return;
      }
    }

    if (newQty === 0) {
      this.state.items.delete(variantId);
    } else {
      this.state.items.set(variantId, {
        qty: newQty,
        price,
        protein: this.parseNutrient(card.dataset.protein),
        fiber: this.parseNutrient(card.dataset.fiber),
        phytonutrients: this.parseNutrient(card.dataset.phytonutrients),
      });
    }

    const qtyDisplay = card.querySelector('.byb__qty-value');
    const minusBtn = card.querySelector('.byb__qty-btn--minus');
    qtyDisplay.textContent = newQty;
    minusBtn.disabled = newQty === 0;
    card.classList.toggle('byb__product-card--active', newQty > 0);

    this.updateUI();
  }

  getSubtotal() {
    let total = 0;
    this.state.items.forEach(({ qty, price }) => {
      total += qty * price;
    });
    return total;
  }

  getTotalItems() {
    let count = 0;
    this.state.items.forEach(({ qty }) => {
      count += qty;
    });
    return count;
  }

  parseNutrient(value) {
    if (value == null || value === '') return 0;
    const match = String(value).match(/[\d.]+/);
    return match ? parseFloat(match[0]) : 0;
  }

  getNutrients() {
    let protein = 0;
    let fiber = 0;
    let phytonutrients = 0;

    this.state.items.forEach(({ qty, protein: p = 0, fiber: f = 0, phytonutrients: phy = 0 }) => {
      protein += qty * p;
      fiber += qty * f;
      phytonutrients += qty * phy;
    });

    return {
      protein: Math.round(protein),
      fiber: Math.round(fiber),
      phytonutrients: Math.round(phytonutrients),
    };
  }

  getDiscountPercent(subtotalCents) {
    const subtotal = subtotalCents / 100;
    const { tier1Threshold, tier1Discount, tier2Threshold, tier2Discount, subscribeExtra } = this.config;
    const isSubscribe = this.state.purchaseType === 'subscribe';
    let discount = 0;

    if (subtotal >= tier2Threshold) {
      discount = tier2Discount;
    } else if (subtotal >= tier1Threshold) {
      discount = tier1Discount;
    }

    if (isSubscribe && subtotal >= this.config.minThreshold) {
      discount += subscribeExtra;
    }

    return discount;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }

  updateUI() {
    const subtotalCents = this.getSubtotal();
    const subtotalDollars = subtotalCents / 100;

    this.updateSubscribeToggle(subtotalDollars);

    const totalItems = this.getTotalItems();
    const atMax = this.config.maxItems > 0 && totalItems >= this.config.maxItems;
    if (this.maxItemsMsg) this.maxItemsMsg.hidden = !atMax;

    const discountPct = this.getDiscountPercent(subtotalCents);
    let discountCents = 0;
    this.state.items.forEach(({ qty, price }) => {
      discountCents += Math.floor(price * (discountPct / 100)) * qty;
    });
    const totalCents = subtotalCents - discountCents;
    const meetsMin = subtotalDollars >= this.config.minThreshold;

    this.updateProgressBar(subtotalDollars);
    this.updateMarkers(subtotalDollars);
    this.updateSidebar(totalItems, subtotalCents, discountCents, discountPct, totalCents, meetsMin, subtotalDollars);
    this.updateMobileFooter(totalItems, subtotalCents, discountPct, totalCents);
    this.updateCheckoutButtons(meetsMin, totalItems);
  }

  updateProgressBar(subtotalDollars) {
    // Build breakpoints dynamically from the DOM marker positions so that
    // any changes to left% in the Liquid are automatically reflected here.
    const breakpoints = [...this.markers]
      .filter((m) => m.style.left)
      .map((m) => ({ value: parseFloat(m.dataset.value), pct: parseFloat(m.style.left) }))
      .sort((a, b) => a.value - b.value);

    let displayPct = 2;

    if (subtotalDollars > 0 && breakpoints.length > 0) {
      if (subtotalDollars <= breakpoints[0].value) {
        displayPct = 2 + (subtotalDollars / breakpoints[0].value) * (breakpoints[0].pct - 2);
      } else {
        let matched = false;
        for (let i = 0; i < breakpoints.length - 1; i++) {
          if (subtotalDollars <= breakpoints[i + 1].value) {
            const span = breakpoints[i + 1].value - breakpoints[i].value;
            const pctSpan = breakpoints[i + 1].pct - breakpoints[i].pct;
            displayPct = breakpoints[i].pct + ((subtotalDollars - breakpoints[i].value) / span) * pctSpan;
            matched = true;
            break;
          }
        }
        if (!matched) displayPct = 100;
      }
    }

    this.progressFill.style.width = `${Math.min(100, displayPct)}%`;
  }

  updateMarkers(subtotalDollars) {
    this.markers.forEach((marker) => {
      const value = parseFloat(marker.dataset.value);
      marker.classList.toggle('byb__marker--reached', subtotalDollars >= value);
    });
  }

  updateSubscribeToggle(subtotalDollars) {
    const subscribeBtn = this.querySelector('[data-purchase-type="subscribe"]');
    if (!subscribeBtn) return;

    const meetsT1 = subtotalDollars >= this.config.tier1Threshold;
    subscribeBtn.disabled = !meetsT1;
    subscribeBtn.classList.toggle('byb__toggle-btn--disabled', !meetsT1);

    if (!meetsT1 && this.state.purchaseType === 'subscribe') {
      this.state.purchaseType = 'onetime';
      this.toggleBtns.forEach((btn) => {
        btn.classList.toggle('byb__toggle-btn--active', btn.dataset.purchaseType === 'onetime');
      });
      this.updateDiscountLabels();
    }
  }

  updateSidebar(totalItems, subtotalCents, discountCents, discountPct, totalCents, meetsMin, subtotalDollars) {
    if (this.sidebarItems) this.sidebarItems.textContent = totalItems;

    const { protein, fiber, phytonutrients } = this.getNutrients();
    if (this.sidebarProtein) this.sidebarProtein.textContent = `${protein}g`;
    if (this.sidebarFiber) this.sidebarFiber.textContent = `${fiber}g`;
    if (this.sidebarPhytonutrients) this.sidebarPhytonutrients.textContent = `${phytonutrients}mg`;
    if (this.phytonutrientsRow) this.phytonutrientsRow.hidden = phytonutrients <= 0;

    if (this.sidebarSubtotal) this.sidebarSubtotal.textContent = this.formatMoney(subtotalCents);

    if (this.discountRow) {
      if (discountPct > 0) {
        this.discountRow.style.display = '';
        if (this.sidebarDiscount) this.sidebarDiscount.textContent = `-${this.formatMoney(discountCents)} (${discountPct}%)`;
      } else {
        this.discountRow.style.display = 'none';
      }
    }

    if (this.sidebarTotal) this.sidebarTotal.textContent = this.formatMoney(totalCents);

    if (this.thresholdMsg && this.remainingAmount) {
      if (!meetsMin && totalItems > 0) {
        const remaining = this.config.minThreshold - subtotalDollars;
        this.remainingAmount.textContent = `$${remaining.toFixed(2)}`;
        this.thresholdMsg.removeAttribute('hidden');
      } else {
        this.thresholdMsg.setAttribute('hidden', '');
      }
    }
  }

  updateMobileFooter(totalItems, subtotalCents, discountPct, totalCents) {
    if (this.mobileItems) this.mobileItems.textContent = `${totalItems} item${totalItems !== 1 ? 's' : ''}`;

    if (this.mobileSubtotal) {
      this.mobileSubtotal.textContent = this.formatMoney(subtotalCents);
      this.mobileSubtotal.classList.toggle('byb__mobile-subtotal--strike', discountPct > 0);
    }

    if (this.mobileTotal) this.mobileTotal.textContent = this.formatMoney(totalCents);
  }

  updateCheckoutButtons(meetsMin, totalItems) {
    const enabled = meetsMin && totalItems > 0;
    if (this.checkoutBtn) this.checkoutBtn.disabled = !enabled;
    if (this.mobileCheckoutBtn) this.mobileCheckoutBtn.disabled = !enabled;
  }

  async addToCart() {
    if (this.checkoutBtn) this.checkoutBtn.classList.add('byb__checkout-btn--loading');
    if (this.mobileCheckoutBtn) this.mobileCheckoutBtn.classList.add('byb__checkout-btn--loading');
    this.hideCheckoutError();

    const items = [];
    const isSubscribe = this.state.purchaseType === 'subscribe';

    let sellingPlanId = '';
    if (isSubscribe) {
      const subtotalDollars = this.getSubtotal() / 100;
      if (subtotalDollars >= this.config.tier2Threshold) {
        sellingPlanId = this.config.sellingPlanIdTier2;
      } else if (subtotalDollars >= this.config.tier1Threshold) {
        sellingPlanId = this.config.sellingPlanIdTier1;
      }
    }

    this.state.items.forEach(({ qty }, variantId) => {
      const item = {
        id: parseInt(variantId, 10),
        quantity: qty,
        properties: { _bundle_builder: true },
      };
      if (sellingPlanId) {
        item.selling_plan = parseInt(sellingPlanId, 10);
      }
      items.push(item);
    });

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json().catch(() => ({}));
        throw new Error(errorData.description || 'Some items are no longer available. Please refresh and try again.');
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { cart } }));

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } catch (err) {
      console.error('Bundle add-to-cart error:', err);
      this.showCheckoutError(err.message);
    } finally {
      if (this.checkoutBtn) this.checkoutBtn.classList.remove('byb__checkout-btn--loading');
      if (this.mobileCheckoutBtn) this.mobileCheckoutBtn.classList.remove('byb__checkout-btn--loading');
    }
  }

  showCheckoutError(message) {
    if (!this.checkoutError) return;
    this.checkoutError.textContent = message;
    this.checkoutError.removeAttribute('hidden');
  }

  hideCheckoutError() {
    if (!this.checkoutError) return;
    this.checkoutError.textContent = '';
    this.checkoutError.setAttribute('hidden', '');
  }
}

if (!customElements.get('build-your-bundle')) {
  customElements.define('build-your-bundle', BuildYourBundle);
}
